import axios from 'axios';
import { BACKEND_BASE_URL } from '@/config/backend';
import { adminApi } from '@/config/adminApi';

const BAN_INFO_STORAGE_KEY = 'banInfo';
const BANNED_CODE = 'USER_BANNED';

// A session that was already signed in when the ban landed never passes back
// through /callback, so its API calls start failing with 403 USER_BANNED.
// Catch that on every HTTP path to the backend and send the user to /banned,
// which reads the cached details from localStorage.
function isBanPayload(status, data) {
  return status === 403 && data?.code === BANNED_CODE;
}

function showBannedScreen(router, data) {
  const { code, ...info } = data;
  try {
    window.localStorage.setItem(BAN_INFO_STORAGE_KEY, JSON.stringify(info));
  } catch {
    // Storage unavailable; the banned page falls back to its defaults.
  }
  if (router.currentRoute.value.name !== 'Banned') {
    router.replace({ name: 'Banned' });
  }
}

function isBackendRequest(input) {
  const url = typeof input === 'string' ? input : input?.url;
  return typeof url === 'string' && url.startsWith(BACKEND_BASE_URL);
}

export function installBanGuard(router) {
  const onAxiosError = (error) => {
    const { status, data } = error?.response || {};
    if (isBanPayload(status, data)) showBannedScreen(router, data);
    return Promise.reject(error);
  };
  axios.interceptors.response.use(undefined, onAxiosError);
  adminApi.interceptors.response.use(undefined, onAxiosError);

  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await nativeFetch(input, init);
    if (response.status === 403 && isBackendRequest(input)) {
      // Read a clone so the caller can still consume the original body.
      response.clone().json()
        .then((data) => {
          if (isBanPayload(response.status, data)) showBannedScreen(router, data);
        })
        .catch(() => {});
    }
    return response;
  };
}
